import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { CLOUD_STORAGE_FILES_URL } from '@/consts/urls';
import { getContext } from '@/context';
import getAuthJsonHeaders from '@/requests/common/getAuthJsonHeaders';
import getDriveFolderUid from '@/requests/cloud-storage/getDriveFolderUid';
import { MediaType } from '@/types';

import type { ContentFileAttributes } from '@/types';

type Params = {
  name: string;
  url: string;
  previewUrl?: string;
  type: MediaType;
  toolId: string;
  folderName: string;
  attributes?: ContentFileAttributes;
};

type CreateFileResponse = {
  response?: { uid?: string };
};

const RESOURCE_TYPE_BY_MEDIA: Record<MediaType, string> = {
  [MediaType.IMAGE]: 'PHOTO',
  [MediaType.PHOTO]: 'PHOTO',
  [MediaType.VIDEO]: 'VIDEO',
  [MediaType.AUDIO]: 'AUDIO',
};

/**
 * Drive takes attributes as a list of pairs here, where the creations query
 * reads them back as an object. Undefined values are dropped rather than sent
 * as empty ones — `userReaction` is absent until the user reacts.
 */
const toAttributeList = (attributes: Record<string, string | undefined>) =>
  Object.entries(attributes)
    .filter(([, value]) => Boolean(value))
    .map(([property, value]) => ({ property, value }));

/**
 * Stores one already-generated result in the user's Drive and answers with the
 * uid it was stored under.
 *
 * The free-quota submit (`guest/`) does not write its results to Drive — its
 * poll answers carry no `driveUid` — so a free or onboarding generation would
 * live only in memory and vanish from the creations grid as soon as Drive is
 * re-read. This puts the file there after the fact, from the result url the
 * flow already returned; nothing is downloaded or re-uploaded.
 *
 * The parent folder is resolved rather than described: named alone it is not
 * matched against the folder the backend already writes into, and Drive ends up
 * with a second folder of the same name per run. `tool` and `category` mirror
 * what a paid submit's `driveOptions` produce, so a file stored here is
 * filtered, grouped and parsed like any other.
 *
 * Resolves to `null` on any failure: the generation itself succeeded and is
 * already on screen, so a failed store costs the Drive copy, never the run.
 */
const addFileToDrive = async ({
  name,
  url,
  previewUrl,
  type,
  toolId,
  folderName,
  attributes,
}: Params): Promise<string | null> => {
  try {
    const parentFolderUid = await getDriveFolderUid(folderName);

    const response = await getContext().utils.api.fetch(
      CLOUD_STORAGE_FILES_URL,
      {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify({
          name,
          sourceUrl: url,
          ...(parentFolderUid && { parentFolderUid }),
          content: {
            type: 'STANDALONE',
            resourceType: RESOURCE_TYPE_BY_MEDIA[type],
          },
          preview: { url: previewUrl ?? url },
          attributes: toAttributeList({ tool: toolId, ...attributes }),
        }),
      }
    );

    if (!response.ok) return null;

    const data: CreateFileResponse = await response.json();

    return data?.response?.uid ?? null;
  } catch (error) {
    errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);

    return null;
  }
};

export default addFileToDrive;
